import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { CenteredSpinner } from '../../components/ui/Spinner';
import {
  DEFAULT_FILTERS,
  allTags,
  bulkSummary,
  filterStaffRows,
  publishBlocker,
  rowLabel,
  statusLabel,
  tabCounts,
  type StaffFilters,
} from '../../lib/portalInspiration/staffView';
import { INSPIRATION_USAGES, INSPIRATION_USAGE_LABELS, type StaffInspirationRow } from '../../types/portalInspiration';
import type { Account } from '../../types/account';
import { listAccounts } from '../accounts';
import { bulkInspiration, listInspiration } from './api';
import { draftEntry } from './draftClient';
import { InspirationEditDialog } from './InspirationEditDialog';
import { InspirationUploadPanel } from './InspirationUploadPanel';

// /tools/inspiration — the STAFF page for the client portal's Inspiration library.
//
// Upload (single or bulk) → each file becomes a DRAFT entry → write or AI-draft the words → choose usage,
// tags and who can see it → publish. Clients only ever see published entries inside their audience.
//
// ⚠️ ON-DEMAND AI: the bulk "Draft with AI" below is the same button pressed for several selected entries,
// run one at a time (each request carries its own frames). Nothing drafts on load or on open.

const STATUS_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];
const USAGE_FILTER = [{ value: '', label: 'Any use' }, ...INSPIRATION_USAGES.map((u) => ({ value: u, label: INSPIRATION_USAGE_LABELS[u] }))];

export function InspirationToolPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<StaffFilters>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // Files just uploaded in this tab: the best source of frames for a later Draft (no download).
  const localFiles = useRef(new Map<string, File>());

  const rowsQuery = useQuery({
    queryKey: ['portal-inspiration'],
    queryFn: listInspiration,
    // Poll only while a file is still being processed, so the card flips to ready by itself. (Not an AI call.)
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.asset && !r.asset.deleted && r.asset.lifecycle !== 'ready' && r.asset.lifecycle !== 'failed') ? 10_000 : false,
  });
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const all = useMemo(() => rowsQuery.data ?? [], [rowsQuery.data]);
  const rows = useMemo(() => filterStaffRows(all, filters), [all, filters]);
  const counts = useMemo(() => tabCounts(all), [all]);
  const tagOptions = useMemo(() => [{ value: '', label: 'Any tag' }, ...allTags(all).map((t) => ({ value: t, label: t }))], [all]);
  const open = all.find((r) => r.entry.id === openId) ?? null;
  const selectedRows = all.filter((r) => selected.has(r.entry.id));

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['portal-inspiration'] });
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function runBulk(op: 'publish' | 'unpublish' | 'delete') {
    setBusy(op);
    try {
      const results = await bulkInspiration(op, [...selected]);
      const bad = results.filter((r) => !r.ok);
      const message = `${bulkSummary(results)}${bad.length > 0 ? ` First problem: ${bad[0]!.error}` : ''}`;
      if (bad.length > 0) toast.error(message);
      else toast.success(message);
      setSelected(new Set(bad.map((b) => b.id)));
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
      setConfirmBulkDelete(false);
    }
  }

  // ⚠️ ON-DEMAND: the button below is the only trigger.
  async function runBulkDraft() {
    const targets = selectedRows.filter((r) => r.entry.status === 'draft' && r.asset && r.asset.lifecycle === 'ready');
    if (targets.length === 0) {
      toast.error('Choose draft entries whose file has finished processing.');
      return;
    }
    let ok = 0;
    const problems: string[] = [];
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i]!;
      setBusy(`Drafting ${i + 1} of ${targets.length}…`);
      try {
        await draftEntry({ entryId: r.entry.id, mediaType: r.entry.mediaType, asset: r.asset, file: localFiles.current.get(r.entry.id) ?? null });
        ok++;
      } catch (err) {
        problems.push(`${rowLabel(r)}: ${err instanceof Error ? err.message : 'failed'}`);
      }
    }
    setBusy(null);
    refresh();
    if (problems.length === 0) toast.success(`Drafted ${ok} with AI. Review each before publishing.`);
    else toast.error(`Drafted ${ok}; ${problems.length} failed (${problems[0]}).`);
  }

  const set = (p: Partial<StaffFilters>) => setFilters((f) => ({ ...f, ...p }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inspiration"
        description="Photo and video ideas Kyck recommends to clients, with a short how-to. Clients can only view them. Upload here, write or AI-draft the words, choose who sees each one, then publish."
      />

      <InspirationUploadPanel
        accounts={(accountsQuery.data ?? []) as Account[]}
        onUploaded={(m) => m.forEach((f, id) => localFiles.current.set(id, f))}
        onChanged={refresh}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {(['video', 'image'] as const).map((t) => (
            <Button key={t} size="sm" variant={filters.mediaType === t ? 'default' : 'outline'} onClick={() => set({ mediaType: t })}>
              {t === 'video' ? 'Videos' : 'Images'} ({counts[t]})
            </Button>
          ))}
        </div>
        <SelectField value={filters.status} onValueChange={(v) => set({ status: v as StaffFilters['status'] })} options={STATUS_OPTIONS} size="sm" aria-label="Status" />
        <SelectField value={filters.usage} onValueChange={(v) => set({ usage: v as StaffFilters['usage'] })} options={USAGE_FILTER} size="sm" aria-label="Use" />
        <SelectField value={filters.tag} onValueChange={(v) => set({ tag: v })} options={tagOptions} size="sm" aria-label="Tag" />
        <Input className="h-8 w-56" placeholder="Search title or tags…" value={filters.search} onChange={(e) => set({ search: e.target.value })} aria-label="Search" />
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <Button size="sm" disabled={busy !== null} onClick={() => void runBulk('publish')}>Publish</Button>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void runBulk('unpublish')}>Unpublish</Button>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void runBulkDraft()}>Draft with AI</Button>
          {confirmBulkDelete ? (
            <span className="flex items-center gap-2">
              Delete {selected.size} entries and their files?
              <Button size="sm" variant="destructive" disabled={busy !== null} onClick={() => void runBulk('delete')}>Yes, delete</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmBulkDelete(false)}>Cancel</Button>
            </span>
          ) : (
            <Button size="sm" variant="ghost" className="text-destructive" disabled={busy !== null} onClick={() => setConfirmBulkDelete(true)}>Delete</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
          {busy && <span className="text-slate-600">{busy}</span>}
        </div>
      )}

      {rowsQuery.isPending ? (
        <CenteredSpinner />
      ) : rowsQuery.isError ? (
        <p className="text-sm text-destructive">Couldn’t load the library.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          {all.length === 0 ? 'Nothing here yet — upload a photo or video above.' : 'No entries match these filters.'}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-5">
          {rows.map((r) => (
            <Card key={r.entry.id} row={r} checked={selected.has(r.entry.id)} onToggle={() => toggle(r.entry.id)} onOpen={() => setOpenId(r.entry.id)} />
          ))}
        </ul>
      )}

      {open && <InspirationEditDialog key={open.entry.id} row={open} localFile={localFiles.current.get(open.entry.id) ?? null} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function Card({ row, checked, onToggle, onOpen }: { row: StaffInspirationRow; checked: boolean; onToggle: () => void; onOpen: () => void }) {
  const st = statusLabel(row);
  const thumb = row.asset?.thumbUrl ?? row.asset?.posterUrl ?? null;
  const blocker = publishBlocker(row);
  return (
    <li className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="relative">
        <button type="button" onClick={onOpen} className="block w-full" aria-label={`Open ${rowLabel(row)}`}>
          {thumb ? (
            <img src={thumb} alt="" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex aspect-[4/5] w-full items-center justify-center bg-slate-100 px-2 text-center text-xs text-slate-500">
              {row.asset?.lifecycle === 'failed' ? 'Failed to process' : 'Processing…'}
            </div>
          )}
        </button>
        <label className="absolute left-2 top-2 rounded bg-white/90 p-1">
          <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Select ${rowLabel(row)}`} />
        </label>
      </div>
      <div className="space-y-1 p-2.5">
        <p className="line-clamp-2 text-sm font-medium text-slate-900">{rowLabel(row)}</p>
        <div className="flex flex-wrap gap-1">
          <Badge variant={st.tone === 'ok' ? 'success' : st.tone === 'warn' ? 'warning' : st.tone === 'bad' ? 'danger' : 'neutral'}>{st.text}</Badge>
          {row.entry.usage && <Badge variant="outline">{INSPIRATION_USAGE_LABELS[row.entry.usage]}</Badge>}
          {row.entry.ai && row.entry.ai.fields.length > 0 && <Badge variant="info">AI draft</Badge>}
        </div>
        {row.entry.status === 'draft' && blocker && <p className="line-clamp-2 text-xs text-slate-500">{blocker}</p>}
        {row.entry.status === 'published' && (
          <p className="text-xs text-slate-500">{row.reach.accountCount} account{row.reach.accountCount === 1 ? '' : 's'}</p>
        )}
      </div>
    </li>
  );
}
